import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Brain, Check, Loader2, Plus, Sparkles, Trash2, X } from "lucide-react";
import { AppLayout } from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { aws as awsClient } from "@/aws/client";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";

export const Route = createFileRoute("/settings/memory")({
  head: () => ({
    meta: [
      { title: "Memory — AskBell Settings" },
      { name: "description", content: "What AskBell remembers about you across chats." },
    ],
  }),
  component: MemorySettingsPage,
});

type MemoryStatus = "active" | "candidate";

type Memory = {
  id: string;
  kind: "role" | "style" | "work_pattern" | "expertise";
  text: string;
  source: "auto" | "user";
  confidence: number;
  status: MemoryStatus;
  evidence_count: number;
  created_at: string;
  last_used_at: string;
};

const KINDS: Memory["kind"][] = ["role", "style", "work_pattern", "expertise"];
const KIND_LABELS: Record<string, string> = {
  role: "Role",
  style: "Style",
  work_pattern: "Work pattern",
  expertise: "Expertise",
};

function MemorySettingsPage() {
  const { user } = useAuth();
  const [memories, setMemories] = useState<Memory[] | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const loadVersion = useRef(0);
  const mutation = useRef(false);
  const [busy, setBusy] = useState(false);
  const [newKind, setNewKind] = useState<Memory["kind"]>("style");
  const [newText, setNewText] = useState("");
  const [adding, setAdding] = useState(false);
  const [forgetOpen, setForgetOpen] = useState(false);
  const [forgetting, setForgetting] = useState(false);
  const [cleanupNotice, setCleanupNotice] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");

  const refresh = async () => {
    if (!user) return;
    const version = ++loadVersion.current;
    setLoading(true);
    setLoadError(null);
    try {
    const results = await Promise.all([
      awsClient.from("profiles").select("memory_enabled, memory_cleanup_seen_at").eq("user_id", user.id).maybeSingle(),
      awsClient
        .from("user_memories")
        .select("id, kind, text, source, confidence, status, evidence_count, created_at, last_used_at")
        .eq("user_id", user.id)
        .in("status", ["active", "candidate"])
        .order("status", { ascending: false })
        .order("last_used_at", { ascending: false })
        .order("id", { ascending: true }),
      awsClient.from("memory_revisions").select("created_at").eq("user_id", user.id).eq("source", "cleanup").eq("state", "active").order("created_at", { ascending: false }).limit(1),
    ]);
    if (version !== loadVersion.current) return;
    for (const result of results) if (result.error) throw new Error(result.error.message);
    const [{ data: profile }, { data: mems }, { data: revisions }] = results;
    if (!profile) throw new Error("Your memory settings could not be found.");
    setEnabled(profile?.memory_enabled !== false);
    setMemories((mems ?? []) as Memory[]);
    const cleanupAt = revisions?.[0]?.created_at;
    setCleanupNotice(Boolean(cleanupAt && (!profile?.memory_cleanup_seen_at || new Date(cleanupAt).getTime() > new Date(profile.memory_cleanup_seen_at).getTime())));
    } catch (error) {
      if (version === loadVersion.current) setLoadError(error instanceof Error ? error.message : "Couldn't load memory settings.");
    } finally {
      if (version === loadVersion.current) setLoading(false);
    }
  };

  useEffect(() => {
    setMemories(null);
    void refresh();
    return () => { loadVersion.current++; };
  }, [user?.id]);

  const mutate = async (action: () => Promise<void>, failure: string) => {
    if (!user || mutation.current || loading || loadError) return;
    mutation.current = true;
    setBusy(true);
    try { await action(); }
    catch (error) { toast.error(error instanceof Error ? error.message : failure); }
    finally { mutation.current = false; setBusy(false); }
  };

  const dismissCleanupNotice = async () => {
    if (!user) return;
    await mutate(async () => {
      const { error } = await awsClient.from("profiles").update({ memory_cleanup_seen_at: new Date().toISOString() }).eq("user_id", user.id);
      if (error) throw new Error("Couldn't dismiss the cleanup notice.");
      setCleanupNotice(false);
    }, "Couldn't dismiss the cleanup notice.");
  };

  const toggleEnabled = async (val: boolean) => {
    if (!user) return;
    await mutate(async () => {
    const { error } = await awsClient.from("profiles").update({ memory_enabled: val }).eq("user_id", user.id);
    if (error) throw new Error("Couldn't update setting.");
    setEnabled(val);
    }, "Couldn't update setting.");
  };

  const handleAdd = async () => {
    if (!user || !newText.trim()) return;
    await mutate(async () => {
    setAdding(true);
    try {
    const { data, error } = await awsClient
      .from("user_memories")
      .insert({
        user_id: user.id,
        kind: newKind,
        text: newText.trim(),
        source: "user",
        confidence: 1,
        status: "active",
      })
      .select("id, kind, text, source, confidence, status, evidence_count, created_at, last_used_at")
      .single();
    if (error || !data) throw new Error("Couldn't save memory.");
    setNewText("");
    setMemories((prev) => [data as Memory, ...(prev ?? [])]);
    } finally { setAdding(false); }
    }, "Couldn't save memory.");
  };

  const startEdit = (m: Memory) => {
    if (m.source !== "user") return;
    setEditingId(m.id);
    setEditText(m.text);
  };

  const saveEdit = async (id: string) => {
    if (!editText.trim()) return;
    await mutate(async () => {
    const { error } = await awsClient.from("user_memories").update({ text: editText.trim() }).eq("id", id);
    if (error) throw new Error("Couldn't update memory.");
    setMemories((prev) => (prev ?? []).map((m) => (m.id === id ? { ...m, text: editText.trim() } : m)));
    setEditingId(null);
    }, "Couldn't update memory.");
  };

  const handleDelete = async (id: string) => {
    await mutate(async () => {
    const { error } = await awsClient.from("user_memories").delete().eq("id", id);
    if (error) throw new Error("Couldn't delete memory.");
    setMemories((prev) => prev?.filter((m) => m.id !== id) ?? prev);
    }, "Couldn't delete memory.");
  };

  const handleForgetAll = async () => {
    if (!user) return;
    await mutate(async () => {
    setForgetting(true);
    try {
    const { error } = await awsClient.from("user_memories").delete().eq("user_id", user.id);
    if (error) throw new Error("Couldn't clear memories.");
    setForgetOpen(false);
    setMemories([]);
    toast.success("All saved memories cleared.");
    } finally { setForgetting(false); }
    }, "Couldn't clear memories.");
  };

  const activeMemories = memories?.filter((m) => m.status === "active") ?? [];
  const candidates = memories?.filter((m) => m.status === "candidate") ?? [];

  return (
    <AppLayout>
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-3xl mx-auto">
          <div className="mb-4 flex items-center gap-2 text-ui-base">
            <Link to="/chat" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to chat
            </Link>
          </div>

          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-ui-md bg-primary/10 text-primary">
              <Brain className="h-5 w-5" />
            </div>
            <h1 className="text-ui-xl font-bold text-foreground">Memory</h1>
          </div>
          <p className="text-ui-base text-muted-foreground mb-6">
            AskBell remembers durable things about you — your role, how you like answers, the kinds of work you do, and your broad expertise. These hints are used across chats, but never include numbers, file names, or project specifics.
          </p>

          {loadError && (
            <div role="alert" className="rounded-ui-md border border-destructive/30 bg-destructive/5 p-4 mb-6">
              <h2 className="text-ui-base font-bold">Couldn't load memory settings</h2>
              <p className="text-ui-base text-muted-foreground mt-1">{loadError}</p>
              <Button variant="outline" size="sm" className="mt-3" disabled={loading} onClick={() => void refresh()}>Retry</Button>
            </div>
          )}

          <fieldset disabled={busy || loading || Boolean(loadError) || memories === null} className="min-w-0 space-y-0 disabled:opacity-60">
          {cleanupNotice && (
            <div className="rounded-ui-md border border-primary/30 bg-primary/10 p-4 mb-6 flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-ui-base font-bold text-foreground">Your memory profile was tidied</p>
                <p className="text-ui-sm text-muted-foreground mt-0.5">
                  AskBell ran a cleanup pass to remove project-specific or time-bound memories and keep only durable profile hints. Review the list below and delete anything that still looks off.
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={dismissCleanupNotice} aria-label="Dismiss" className="h-8 w-8 shrink-0">
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          <div className="rounded-ui-md border border-border bg-card p-4 mb-6 flex items-center justify-between gap-4">
            <div>
              <Label htmlFor="memory-toggle" className="text-ui-base font-bold">Let AskBell remember things about me</Label>
              <p className="text-ui-sm text-muted-foreground mt-0.5">
                When off, no new memories are saved and existing ones aren't shown to AskBell. They stay here for if you turn it back on.
              </p>
            </div>
            <Switch id="memory-toggle" disabled={busy || loading || Boolean(loadError) || memories === null} checked={enabled} onCheckedChange={toggleEnabled} />
          </div>

          <div className="rounded-ui-md border border-border bg-card p-4 mb-6">
            <h2 className="text-ui-base font-bold mb-3">Add a memory</h2>
            <div className="flex flex-col sm:flex-row gap-2">
              <Select disabled={busy || loading || Boolean(loadError) || memories === null} value={newKind} onValueChange={(v) => setNewKind(v as Memory["kind"])}>
                <SelectTrigger aria-label="Memory kind" className="w-full sm:w-44 sm:shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KINDS.map((k) => (
                    <SelectItem key={k} value={k}>{KIND_LABELS[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                aria-label="New memory"
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAdd(); } }}
                placeholder="e.g. Prefers concise prose without bullets"
              />
              <Button onClick={handleAdd} disabled={!newText.trim() || adding} className="gap-1.5">
                <Plus className="h-4 w-4" />
                Add
              </Button>
            </div>
          </div>

          {loading ? (
            <div role="status" aria-label="Loading memory settings" className="flex justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : memories !== null && !loadError ? (
            <>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-ui-base font-bold">Active profile hints</h2>
                <span className="text-ui-sm text-muted-foreground">{activeMemories.length} / 30</span>
              </div>
              {activeMemories.length === 0 ? (
                <p className="text-ui-base text-muted-foreground text-center py-6 border border-dashed border-border rounded-ui-md mb-8">
                  No active memories yet. AskBell will start learning as you chat.
                </p>
              ) : (
                <ul className="space-y-2 mb-8">
                  {activeMemories.map((m) => (
                    <MemoryRow
                      key={m.id}
                      memory={m}
                      editing={editingId === m.id}
                      editText={editText}
                      onEditChange={setEditText}
                      onStartEdit={() => startEdit(m)}
                      onSave={() => saveEdit(m.id)}
                      onCancel={() => setEditingId(null)}
                      onDelete={() => handleDelete(m.id)}
                    />
                  ))}
                </ul>
              )}

              {candidates.length > 0 && (
                <>
                  <h2 className="text-ui-base font-bold mb-3">Learning candidates</h2>
                  <p className="text-ui-sm text-muted-foreground mb-3">
                    These appear after AskBell notices the same pattern in at least three separate conversations. You can delete any that look wrong.
                  </p>
                  <ul className="space-y-2 mb-8 opacity-80">
                    {candidates.map((m) => (
                      <MemoryRow
                        key={m.id}
                        memory={m}
                        editing={false}
                        editText=""
                        onEditChange={() => {}}
                        onStartEdit={() => {}}
                        onSave={() => {}}
                        onCancel={() => {}}
                        onDelete={() => handleDelete(m.id)}
                      />
                    ))}
                  </ul>
                </>
              )}
            </>
          ) : null}

          {memories && memories.length > 0 && (
            <div className="mt-8 pt-4 border-t border-border">
              <Button variant="destructive" onClick={() => setForgetOpen(true)} className="gap-1.5">
                <Trash2 className="h-4 w-4" />
                Forget everything about me
              </Button>
            </div>
          )}
          </fieldset>
        </div>
      </div>

      <AlertDialog open={forgetOpen} onOpenChange={(open) => { if (!mutation.current) setForgetOpen(open); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Forget everything?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes every memory AskBell has about you. It can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={forgetting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={forgetting}
              onClick={(e) => { e.preventDefault(); handleForgetAll(); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {forgetting ? "Forgetting…" : "Forget everything"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}

function MemoryRow({
  memory,
  editing,
  editText,
  onEditChange,
  onStartEdit,
  onSave,
  onCancel,
  onDelete,
}: {
  memory: Memory;
  editing: boolean;
  editText: string;
  onEditChange: (v: string) => void;
  onStartEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete: () => void;
}) {
  return (
    <li className="flex items-start gap-3 rounded-ui-md border border-border bg-card px-3 py-2.5">
      <span className="text-ui-xs uppercase font-bold tracking-wider text-primary bg-primary/10 rounded px-1.5 py-0.5 mt-0.5 shrink-0">
        {KIND_LABELS[memory.kind] ?? memory.kind}
      </span>
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="flex gap-2">
            <Input
              aria-label="Edit memory text"
              value={editText}
              onChange={(e) => onEditChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onSave(); } if (e.key === "Escape") { onCancel(); } }}
              className="h-8 text-ui-base"
              autoFocus
            />
            <Button size="icon" variant="ghost" aria-label="Save memory" disabled={!editText.trim()} onClick={onSave} className="h-8 w-8 shrink-0">
              <Check className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" aria-label="Cancel editing" onClick={onCancel} className="h-8 w-8 shrink-0">
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <>
            <p className="text-ui-base text-foreground">{memory.text}</p>
            <p className="text-ui-sm text-muted-foreground mt-0.5">
              {memory.source === "user" ? "Added by you" : "Learned from conversations"}
              {memory.status === "candidate" && (
                <> · seen in {memory.evidence_count} chat{memory.evidence_count === 1 ? "" : "s"}</>
              )}
            </p>
          </>
        )}
      </div>
      {memory.source === "user" && !editing && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onStartEdit}
          className="h-8 w-8 text-muted-foreground hover:text-foreground"
          aria-label="Edit memory"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-pencil"><path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.62l4.349-1.32a2 2 0 0 0 .83-.497z"/><path d="m15 5 4 4"/></svg>
        </Button>
      )}
      <Button
        variant="ghost"
        size="icon"
        onClick={onDelete}
        className="h-8 w-8 text-muted-foreground hover:text-destructive"
        aria-label="Delete memory"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </li>
  );
}
